import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';

const LOGO_MAIN = "https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/8jqmf57d_Logo-V3-Vector-FINAL.png";
const GENERAC_BADGE = "https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/40wcnfw6_Generac-Installer.png";
const TESLA_BADGE = "https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/vcyr36jn_tesla-certified-installer.jpg";

const Footer = () => {
  const services = [
    { name: 'Residential Electrical', slug: 'residential-electrical' },
    { name: 'Commercial Electrical', slug: 'commercial-electrical' },
    { name: 'EV Charger Installation', slug: 'ev-charger-installation' },
    { name: 'Panel Replacement', slug: 'panel-replacement' },
    { name: 'Generator Installation', slug: 'generator-installation' },
    { name: 'Lighting Installation', slug: 'lighting-installation' }
  ];

  return (
    <footer className="bg-secondary-dark border-t border-white/5" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img 
              src={LOGO_MAIN} 
              alt="Island Coastal Electric" 
              className="h-24 w-auto mb-6"
              data-testid="footer-logo"
            />
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Master Electricians serving Vancouver Island with professional, reliable electrical services for residential, commercial, and industrial clients.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-white/40 hover:text-electric-teal transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-white/40 hover:text-electric-teal transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-white/40 hover:text-electric-teal transition-colors" data-testid="social-linkedin">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.slug}>
                  <Link 
                    to={`/services/${service.slug}`}
                    className="text-white/60 hover:text-electric-teal transition-colors text-sm"
                    data-testid={`footer-service-${service.slug}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-about">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-gallery">
                  Real Results
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-faq">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-blog">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/areas" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-areas">
                  Areas We Serve
                </Link>
              </li>
              <li>
                <Link to="/book-estimate" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-book">
                  Book an Estimate
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-contact">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/admin" className="text-white/60 hover:text-electric-teal transition-colors text-sm" data-testid="footer-admin">
                  Admin Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+17789602377" 
                  className="flex items-start gap-3 text-white/60 hover:text-electric-teal transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">778.960.2377</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@islandcoastalelectric.ca" 
                  className="flex items-start gap-3 text-white/60 hover:text-electric-teal transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">info@islandcoastalelectric.ca</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:lee@islandcoastalelectric.ca" 
                  className="flex items-start gap-3 text-white/60 hover:text-electric-teal transition-colors"
                  data-testid="footer-email-lee"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">lee@islandcoastalelectric.ca</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">2668 Sooke Road<br />Victoria, BC V9B 1Y6</span>
                </div>
              </li>
            </ul>
            <div className="mt-6 p-4 bg-white/5 rounded">
              <p className="text-white/80 text-sm font-semibold">Business Hours</p>
              <p className="text-white/60 text-sm mt-1">Mon-Fri: 7AM-6PM</p>
              <p className="text-white/60 text-sm">Sat: 8AM-4PM</p>
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h4 className="text-white font-bold uppercase tracking-wider mb-4">Service Areas</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {[
              { name: 'Victoria', slug: 'victoria' },
              { name: 'Langford', slug: 'langford' },
              { name: 'Sooke', slug: 'sooke' },
              { name: 'Saanich', slug: 'saanich' },
              { name: 'Duncan', slug: 'duncan' },
              { name: 'Nanaimo', slug: 'nanaimo' },
              { name: 'Sidney', slug: 'sidney' },
              { name: 'Colwood', slug: 'colwood' },
              { name: 'Courtenay', slug: 'courtenay' },
              { name: 'Campbell River', slug: 'campbell-river' },
              { name: 'Parksville', slug: 'parksville' },
              { name: 'Chemainus', slug: 'chemainus' },
              { name: 'Ladysmith', slug: 'ladysmith' },
              { name: 'Oak Bay', slug: 'oak-bay' },
              { name: 'Tofino', slug: 'tofino' },
              { name: 'Comox', slug: 'comox' },
            ].map((area) => (
              <Link
                key={area.slug}
                to={`/areas/${area.slug}`}
                className="text-white/50 hover:text-electric-teal transition-colors text-xs"
                data-testid={`footer-area-${area.slug}`}
              >
                {area.name}
              </Link>
            ))}
            <Link to="/areas" className="text-electric-teal hover:text-white transition-colors text-xs font-semibold" data-testid="footer-all-areas">
              View All Areas
            </Link>
          </div>
        </div>

        {/* Certifications */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h4 className="text-white font-bold uppercase tracking-wider mb-6 text-center">Certified &amp; Trusted</h4>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            <div className="flex flex-col items-center gap-2" data-testid="footer-tesla-badge">
              <img src={TESLA_BADGE} alt="Tesla Certified Installer" className="h-16 md:h-20 w-auto rounded-lg" />
              <span className="text-white/50 text-xs">Tesla Certified</span>
            </div>
            <div className="flex flex-col items-center gap-2" data-testid="footer-generac-badge">
              <img src={GENERAC_BADGE} alt="Generac Certified Installer" className="h-16 md:h-20 w-auto" />
              <span className="text-white/50 text-xs">Generac Certified</span>
            </div>
            <div className="flex flex-col items-center gap-2" data-testid="footer-bch-badge">
              <div className="h-16 md:h-20 flex items-center justify-center px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                <span className="text-electric-teal font-bold text-sm md:text-base text-center leading-tight">BC Hydro<br />Alliance of<br />Energy Professionals</span>
              </div>
              <span className="text-white/50 text-xs">BC Hydro Alliance</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Island Coastal Electric. All rights reserved.
          </p>
          <p className="text-white/40 text-sm">
            Tesla Certified • Generac Certified • BC Hydro Alliance Member • Fully Insured
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
